// Shared vanilla CSS for the material-content renderers (MCSA, MCMA, TrueFalse,
// Dropdown, Ordering, Grouping, Matching, FillInTheBlanks). Prefix: mc-.
// Aggregated into libraryStyles. Common option/panel/scroll scaffolding plus the
// correct/incorrect answer-state borders; renderer-specific bits are added as
// `mc-*` rules here too.
export const materialContentStyles = `
  .mc-root { display: flex; flex-direction: column; height: 100%; }
  .mc-section { display: flex; flex-direction: column; flex: 1 1 0%; min-height: 0; }
  .mc-panel {
    flex: 1 1 0%;
    display: flex;
    flex-direction: column;
    border-color: var(--catchup-lighter-gray);
    border-radius: var(--rad-catchup-xlarge);
    padding-left: 1.25rem;
    padding-right: 1.25rem;
    min-height: 0;
  }
  @media (min-width: 768px) { .mc-panel { padding-left: 0; padding-right: 0; } }
  .mc-scroll { flex: 1 1 0%; min-height: 0; overflow-y: auto; }
  .mc-shrink0 { flex-shrink: 0; }

  /* option list — vertical glass card rows (design prototype) */
  .mc-options { display: flex; flex-direction: column; width: 100%; gap: 10px; }
  .mc-options-center { }
  .mc-option {
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    cursor: pointer;
    margin: 0;
    padding: 14px 16px;
    border-radius: var(--rad-catchup-large);
    background: var(--catchup-white);
    border: 1.5px solid var(--catchup-blue-100);
    transition: transform 150ms, border-color 150ms, box-shadow 150ms, background 150ms;
  }
  .mc-option:hover { transform: translateY(-1px); border-color: var(--catchup-blue-300); }
  /* V4 aas__choice.is-on — no outer ring (it visually bleeds past the border) */
  .mc-option-sel {
    background: var(--brand-50);
    border-color: var(--catchup-blue-300);
  }
  .mc-option-gap { gap: 0.75rem; }
  .mc-correct {
    border-width: 1.5px;
    border-style: solid;
    border-color: var(--color-success);
    background: linear-gradient(160deg, rgba(122,203,164,0.42), rgba(94,170,130,0.24));
    -webkit-backdrop-filter: blur(10px); backdrop-filter: blur(10px);
    box-shadow: inset 1.5px 1.5px 2px rgba(255,255,255,0.5), 0 0 0 2px var(--color-success-ring, rgba(70,150,95,0.22));
    border-radius: var(--rad-catchup-large);
    padding: 14px 16px;
    color: var(--color-success-text);
  }
  .mc-incorrect {
    border-width: 1.5px;
    border-style: solid;
    border-color: var(--color-error);
    background: linear-gradient(160deg, rgba(232,150,128,0.4), rgba(217,97,63,0.22));
    -webkit-backdrop-filter: blur(10px); backdrop-filter: blur(10px);
    box-shadow: inset 1.5px 1.5px 2px rgba(255,255,255,0.5);
    border-radius: var(--rad-catchup-large);
    padding: 14px 16px;
    color: var(--color-error-text);
  }
  .mc-option-body { flex: 1 1 0%; }

  /* locked — answer submitted / time exceeded / duel-contest already answered.
     The full options view stays visible (picked option keeps its highlight) but
     nothing is clickable. The .mc-imgopt__zoom button is left live so learners
     can still inspect an option image. */
  .mc-locked .mc-option, .mc-locked .mc-imgopt, .mc-locked .mc-dot { cursor: default; }
  .mc-locked .mc-option:hover { transform: none; border-color: var(--catchup-blue-100); box-shadow: none; }
  .mc-locked .mc-option-sel:hover { border-color: var(--catchup-blue-300); }
  .mc-locked .mc-correct:hover { border-color: var(--color-success); }
  .mc-locked .mc-incorrect:hover { border-color: var(--color-error); }
  .mc-locked .mc-imgopt:hover { transform: none; border-color: var(--catchup-blue-100); }
  .mc-locked .mc-imgopt.is-sel:hover { border-color: var(--catchup-blue-300); }
  .mc-locked .mc-imgopt.is-correct:hover { border-color: var(--color-success); }
  .mc-locked .mc-imgopt.is-wrong:hover { border-color: var(--color-error); }

  /* CSS radio dot (design spv-opt__dot) — replaces the legacy Icon glyph */
  .mc-dot {
    position: relative;
    width: 20px; height: 20px; flex-shrink: 0;
    border-radius: 50%;
    border: 2px solid var(--catchup-blue-200);
    transition: all var(--duration-base) var(--ease-standard);
  }
  .mc-option-sel .mc-dot { border-color: var(--catchup-blue); }
  .mc-option-sel .mc-dot::after {
    content: ""; position: absolute; inset: 3px; border-radius: 50%;
    background: var(--catchup-blue);
  }
  .mc-correct .mc-dot { border-color: var(--color-success); background: var(--color-success); box-shadow: inset 0 0 0 3px #fff; }
  .mc-incorrect .mc-dot { border-color: var(--color-error); background: var(--color-error); box-shadow: inset 0 0 0 3px #fff; }
  /* Graded: the verdict colours above fill the dot of the CORRECT option whether
     or not the learner chose it, so the learner's OWN pick carries a ring on top —
     "what I answered" must stay readable independently of "what was correct".
     The two box-shadows compose: the inset keeps the dot's donut, the outset draws
     the ring. NOTE: TrueFalse no longer uses this device — it moved the verdict to
     a chip in its own column (see .mc-tf2 "third axis" below). MCSA/MCMA still
     overload the dot and are candidates for the same treatment. */
  .mc-option-picked .mc-dot {
    box-shadow: inset 0 0 0 3px #fff, 0 0 0 2px var(--catchup-white), 0 0 0 4px var(--catchup-blue);
  }
  /* The correct option the learner did NOT pick: keep the green border from
     .mc-correct but leave the dot HOLLOW, so a filled dot always means "I chose
     this one" and never asserts a selection the learner didn't make. */
  .mc-option-key .mc-dot { background: transparent; box-shadow: none; }

  /* "Doğru cevap" / "Senin cevabın" key for the graded MCSA & MCMA option lists,
     so the pick ring is self-explanatory. Same shape as .mc-tf2-legend. */
  .mc-legend { display: flex; flex-wrap: wrap; gap: 16px 20px; margin-top: 14px; padding-top: 12px; border-top: 1px solid var(--catchup-blue-100); }
  .mc-legend-item { display: inline-flex; align-items: center; gap: 8px; font-size: var(--fs-sm); font-weight: 600; color: var(--text-muted); }
  .mc-legend-dot { width: 18px; height: 18px; border-radius: 50%; border: 2px solid var(--catchup-blue-300); background: var(--catchup-white); flex-shrink: 0; }
  /* HOLLOW, matching .mc-option-key above exactly. This swatch was pale-FILLED
     while the dot it describes is transparent, so the legend illustrated a mark
     the list never draws. A legend that disagrees with its grid is worse than no
     legend — the learner trusts it and mis-reads every row. */
  .mc-legend-dot.is-key { border-color: var(--color-success); background: transparent; }
  .mc-legend-dot.is-picked { box-shadow: 0 0 0 2px var(--catchup-white), 0 0 0 4px var(--catchup-blue); }
  .mc-legend .ig-cbx-box { width: 18px; height: 18px; cursor: default; }

  /* graded ✓/✗ marks, right-aligned on the option row (design spv-fb) */
  .mc-fb { margin-left: auto; padding-left: 0.5rem; flex-shrink: 0; font-size: var(--fs-sm); font-weight: 800; }
  .mc-fb--ok { color: var(--color-success-text); }
  .mc-fb--no { color: var(--color-error-text); }

  /* multiplayer answer-reveal name chips pinned on the picked option */
  .mc-picktags { display: inline-flex; align-items: center; gap: 4px; margin-left: auto; padding-left: 0.5rem; flex-shrink: 0; flex-wrap: wrap; justify-content: flex-end; }
  .mc-picktags + .mc-fb { margin-left: 0; }
  .mc-picktag { max-width: 140px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    font-size: var(--fs-sm); font-weight: 700; padding: 2px 10px; border-radius: var(--rad-catchup-full);
    animation: mcPickIn 0.3s var(--ease-standard, ease-out) both; }
  .mc-picktag--ok { color: var(--color-success-text); background: rgba(61,174,114,0.16); box-shadow: inset 0 0 0 1px rgba(61,174,114,0.35); }
  .mc-picktag--no { color: var(--color-error-text); background: rgba(217,97,63,0.14); box-shadow: inset 0 0 0 1px rgba(217,97,63,0.3); }
  @keyframes mcPickIn { from { opacity: 0; transform: translateY(4px) scale(0.85); } to { opacity: 1; transform: none; } }

  /* image options — 2-col card grid (design spv-imgopts / spv-imgopt) */
  .mc-imgopts { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  .mc-imgopt {
    cursor: pointer;
    border-radius: var(--rad-catchup-large);
    overflow: hidden;
    background: var(--catchup-white);
    border: 1.5px solid var(--catchup-blue-100);
    transition: transform 150ms, box-shadow 150ms, border-color 150ms;
    display: flex; flex-direction: column;
  }
  .mc-imgopt:hover { transform: translateY(-1px); border-color: var(--catchup-blue-300); }
  /* the card is keyboard-selectable (role=radio + Enter/Space), so it needs a
     visible focus ring — the default outline is clipped by overflow: hidden */
  .mc-imgopt:focus-visible { outline: none; border-color: var(--catchup-blue); box-shadow: 0 0 0 3px var(--catchup-blue-200); }
  .mc-imgopt.is-sel { border-color: var(--catchup-blue-300); }
  .mc-imgopt.is-correct { border-color: var(--color-success); box-shadow: 0 0 0 3px var(--color-success-ring, rgba(70,150,95,0.22)); }
  .mc-imgopt.is-wrong { border-color: var(--color-error); box-shadow: 0 0 0 3px var(--color-error-ring, rgba(217,97,63,0.18)); }
  /* contain (not cover) so content-bearing option images are never cropped;
     light letterbox background keeps the uniform grid rows tidy.
     The image is NOT a zoom target — it selects like the rest of the card, so it
     inherits the card cursor (pointer, or default when locked). Zoom moved to
     .mc-imgopt__zoom: an onClick on the <img> that stopped propagation made ~80%
     of the card unable to answer, leaving only the label strip clickable. */
  .mc-imgopt__media { position: relative; display: block; }
  .mc-imgopt img { width: 100%; height: 160px; object-fit: contain; display: block; cursor: inherit; background: var(--catchup-gray-50); }
  .mc-imgopt__zoom {
    position: absolute; top: 8px; right: 8px;
    height: 1.5rem; width: 1.5rem; padding: 0.25rem;
    display: flex; align-items: center; justify-content: center;
    border: 1px solid var(--catchup-blue);
    border-radius: var(--rad-catchup-full);
    background: rgba(255, 255, 255, 0.8);
    color: var(--catchup-blue);
    cursor: zoom-in;
  }
  .mc-imgopt__zoom:hover { background: var(--catchup-white); }
  .mc-imgopt__label {
    display: flex; align-items: center; gap: 8px;
    padding: 9px 12px;
    font-weight: 700; font-size: var(--fs-base); color: var(--text-secondary);
  }
  .mc-imgopt.is-sel .mc-imgopt__label { color: var(--brand-dark); background: var(--brand-50); }
  .mc-imgopt .mc-dot { width: 18px; height: 18px; }
  .mc-imgopt.is-sel .mc-dot { border-color: var(--catchup-blue); }
  .mc-imgopt.is-sel .mc-dot::after { content: ""; position: absolute; inset: 3px; border-radius: 50%; background: var(--catchup-blue); }
  .mc-imgopt.is-correct .mc-dot { border-color: var(--color-success); background: var(--color-success); box-shadow: inset 0 0 0 3px #fff; }
  .mc-imgopt.is-wrong .mc-dot { border-color: var(--color-error); background: var(--color-error); box-shadow: inset 0 0 0 3px #fff; }
  /* The learner's-own-pick ring, as .mc-option-picked above. MUST stay after the
     is-correct/is-wrong rules: those carry equal specificity (0,3,0) and would
     otherwise win on source order and drop the ring on image options. */
  .mc-imgopt.is-picked .mc-dot {
    box-shadow: inset 0 0 0 3px #fff, 0 0 0 2px var(--catchup-white), 0 0 0 4px var(--catchup-blue);
  }
  .mc-imgopt.is-key .mc-dot { background: transparent; box-shadow: none; }

  /* option typography (design spv-opt) — scoped to option rows so the shared
     .mc-text used by other renderers keeps its size */
  .mc-option .mc-text { font-size: var(--fs-lg); font-weight: 600; color: var(--text-secondary); }
  .mc-correct .mc-text, .mc-incorrect .mc-text { color: inherit; }

  .mc-text { font-size: var(--fs-xl); white-space: pre-wrap; }
  .mc-xl { font-size: var(--fs-xl); }
  .mc-bold { font-weight: 700; }
  .mc-underline { text-decoration: underline; }

  /* TrueFalse — design-prototype grid (statement | Doğru | Yanlış). Prefix mc-tf2-. */
  .mc-tf2 { display: flex; flex-direction: column; }
  .mc-tf2-head, .mc-tf2-row { display: grid; grid-template-columns: 1fr 64px 64px; align-items: center; gap: 8px; }
  .mc-tf2-head { margin-bottom: 8px; }
  .mc-tf2-col { text-align: center; font-size: var(--fs-sm); font-weight: 700; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.04em; }
  .mc-tf2-row { padding: 12px 0; border-top: 1px solid var(--catchup-blue-100); }
  .mc-tf2-text { font-size: var(--fs-lg); font-weight: 600; color: var(--text-primary); white-space: pre-wrap; }
  .mc-tf2-box { justify-self: center; width: 26px; height: 26px; border-radius: var(--rad-catchup-small); border: 2px solid var(--catchup-blue-300); background: var(--catchup-white); cursor: pointer; color: #fff; font-size: 14px; font-weight: 700; display: flex; align-items: center; justify-content: center; }
  .mc-tf2-box:disabled { cursor: default; }
  .mc-tf2-box.is-on { border-color: var(--catchup-blue); background: var(--catchup-blue); }
  /* Bare key reveal (no learner answer on screen): the key is simply filled. */
  .mc-tf2-box.is-correct { border-color: var(--color-success); background: var(--color-success); }
  /* GRADED, third-axis encoding. A filled box means one thing only — "I marked
     this" — in solve and in review alike, so the fill never doubles as a verdict. */
  .mc-tf2-box.is-marked { border-color: var(--catchup-blue); background: var(--catchup-blue); }
  /* The correct side, marked the SAME way whether or not the learner chose it: a
     green underline beneath the box. One concept, one visual — which is what lets
     the legend below be literally true. */
  .mc-tf2-box.is-key { position: relative; border-color: var(--color-success); }
  .mc-tf2-box.is-key::after {
    content: ""; position: absolute; left: 50%; transform: translateX(-50%);
    bottom: -8px; width: 18px; height: 3px; border-radius: 2px;
    background: var(--color-success);
  }
  /* is-marked and is-key are equal specificity and BOTH set the background, so a
     box that is marked AND correct would resolve by source order alone. State the
     combination explicitly (0,3,0) rather than relying on the order of the two
     rules above — an ordering dependency here silently drops the fill. */
  .mc-tf2-box.is-marked.is-key { border-color: var(--catchup-blue); background: var(--catchup-blue); }
  /* Graded rows: a 3px outcome stripe replaces the old full-width wash. The wash
     only repeated what the verdict chip already says, while crowding the key. */
  .mc-tf2.is-graded .mc-tf2-head,
  .mc-tf2.is-graded .mc-tf2-row { grid-template-columns: 1fr 64px 64px minmax(104px, 152px); }
  .mc-tf2.is-graded .mc-tf2-row { padding: 16px 0; }
  .mc-tf2.is-graded .mc-tf2-text { padding-left: 11px; }
  .mc-tf2.is-graded .mc-tf2-col-verdict { text-align: left; }
  .mc-tf2-row { position: relative; }
  .mc-tf2-stripe { position: absolute; left: 0; top: 8px; bottom: 8px; width: 3px; border-radius: 2px; }
  .mc-tf2-stripe.is-correct { background: var(--color-success); }
  .mc-tf2-stripe.is-wrong { background: var(--color-error); }
  .mc-tf2-stripe.is-blank { background: var(--catchup-blue-300); }
  /* The verdict chip — the third axis, stated in words. */
  .mc-tf2-verdict { justify-self: start; display: inline-flex; align-items: center; gap: 6px; padding: 4px 10px; border-radius: 999px; font-size: var(--fs-sm); font-weight: 700; white-space: nowrap; }
  .mc-tf2-verdict-ic { font-size: 12px; line-height: 1; }
  .mc-tf2-verdict.is-correct { background: var(--color-success-bg); color: var(--color-success-text); }
  .mc-tf2-verdict.is-wrong { background: var(--color-error-bg); color: var(--color-error-text); }
  .mc-tf2-verdict.is-blank { background: color-mix(in srgb, var(--text-muted) 12%, transparent); color: var(--text-secondary); }
  /* Graded-view legend. Both swatches are the literal grid visuals. */
  .mc-tf2-legend { display: flex; flex-wrap: wrap; gap: 16px 20px; margin-top: 14px; padding-top: 12px; border-top: 1px solid var(--catchup-blue-100); }
  .mc-tf2-legend-item { display: inline-flex; align-items: center; gap: 8px; font-size: var(--fs-sm); font-weight: 600; color: var(--text-muted); }
  .mc-tf2-legend-box { width: 18px; height: 18px; border-radius: var(--rad-catchup-small); border: 2px solid var(--catchup-blue-300); background: var(--catchup-white); color: #fff; font-size: 11px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .mc-tf2-legend-box.is-marked { border-color: var(--catchup-blue); background: var(--catchup-blue); }
  .mc-tf2-legend-key { width: 18px; height: 3px; border-radius: 2px; background: var(--color-success); flex-shrink: 0; }
  /* Narrow: the verdict chip drops to its own line rather than crushing the
     statement column. The header label goes with it — the chips are self-labelling. */
  @media (max-width: 560px) {
    .mc-tf2.is-graded .mc-tf2-head,
    .mc-tf2.is-graded .mc-tf2-row { grid-template-columns: 1fr 52px 52px; }
    .mc-tf2.is-graded .mc-tf2-col-verdict { display: none; }
    .mc-tf2.is-graded .mc-tf2-verdict { grid-column: 1 / -1; margin-top: 10px; }
  }

  /* TrueFalse-specific (legacy) */
  .mc-tf-header {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
  }
  .mc-tf-col { width: 50px; }
  .mc-tf-label { font-weight: 700; font-size: var(--fs-lg); }
  .mc-tf-row { display: flex; flex-direction: row; align-items: center; gap: 0.5rem; }
  .mc-tf-cell { display: flex; flex-direction: column; align-items: center; justify-content: center; }
  .mc-tf-text { font-size: var(--fs-xl); padding: 0.5rem; white-space: pre-wrap; }

  /* Dropdown-specific */
  .mc-dd-grid { width: 100%; display: flex; flex-direction: row; flex-wrap: wrap; }
  .mc-dd-cell { width: 100%; }
  @media (min-width: 768px) { .mc-dd-cell { width: 50%; } }
  .mc-dd-pad { margin-left: 0.5rem; margin-right: 0.5rem; }
  .mc-dd-row {
    width: 100%;
    display: flex;
    flex-direction: row;
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
    gap: 0.5rem;
  }
  .mc-dd-num { margin-top: auto; margin-bottom: auto; }
  .mc-dd-field { width: 100%; position: relative; }
  .mc-dd-placeholder {
    width: var(--w-catchup-activity-media-box-item);
    height: var(--h-catchup-activity-media-box-item);
    border-width: 1px;
    border-style: dashed;
    border-radius: var(--rad-catchup-xlarge);
    border-color: var(--catchup-blue);
  }
  .mc-dd-placeholder-inner {
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 0.5rem 1rem;
  }
  .mc-dd-pill {
    position: absolute;
    top: -0.5rem;
    right: 0.75rem;
    background-color: var(--catchup-white);
  }
  .mc-italic { font-style: italic; }

  /* Ordering-specific (drag/drop). NOTE: legacy classes h-/w-catchup-activity-box-item
     and ring-blue-*/bg-blue-50 were undefined utilities (no theme entry) → no-ops, so
     they are intentionally NOT reproduced. ring-2 rendered the Tailwind default blue
     ring, preserved here as a 2px rgba(59,130,246,0.5) box-shadow. */
  .mc-ord-drag { position: fixed; pointer-events: none; z-index: 50; opacity: 0.8; }
  .mc-ord-chip-text {
    border-color: var(--catchup-blue);
    border-width: 2px;
    border-style: solid;
    padding: 0.5rem 0.75rem;
    border-radius: var(--rad-catchup-xlarge);
    background-color: var(--catchup-white);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1);
  }
  .mc-ord-chip-media {
    border-color: var(--catchup-blue);
    border-width: 2px;
    border-style: solid;
    padding: 0.25rem 0.5rem;
    border-radius: var(--rad-catchup-xlarge);
    background-color: var(--catchup-white);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1);
  }
  .mc-ord-item { width: 100%; }
  .mc-ord-row {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-top: 1rem;
    margin-bottom: 1rem;
    margin-left: 0.5rem;
    margin-right: 0.5rem;
  }
  .mc-ord-numcol { margin-right: 0.75rem; }
  .mc-ord-numwrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 300ms cubic-bezier(0.4, 0, 0.2, 1);
    overflow-y: auto;
  }
  .mc-ord-circle {
    border-width: 2px;
    border-style: solid;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    transition: all 300ms cubic-bezier(0.4, 0, 0.2, 1);
    border-radius: var(--rad-catchup-full);
    width: 50px;
    height: 50px;
  }
  .mc-ord-circle-sel { border-color: var(--catchup-light-gray); }
  .mc-ord-circle-def { border-color: var(--catchup-blue); }
  .mc-ord-zone { flex: 1 1 0%; transition: all 200ms cubic-bezier(0.4, 0, 0.2, 1); }
  .mc-ord-dragging { opacity: 0.4; }
  .mc-ord-selected { box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5); }
  .mc-ord-droptarget { box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5); }
  .mc-ord-box {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-width: 2px;
    border-style: solid;
    border-radius: var(--rad-catchup-xlarge);
    cursor: pointer;
    padding: 0.75rem;
  }
  .mc-ord-box-text { height: var(--h-catchup-activity-text-box-item); }
  .mc-ord-box-media { height: var(--h-catchup-activity-media-box-item); }
  .mc-ord-box-correct { border-color: var(--color-success); background: var(--color-success-bg); }
  .mc-ord-box-incorrect { border-color: var(--color-error); background: var(--color-error-bg); }
  .mc-ord-box-default { border-color: var(--catchup-blue); }

  /* Matching-specific (drag/drop). Reuses mc-ord-drag overlay, mc-ord-box-text/media
     heights, and mc-ord-box-correct/-incorrect/-default borders. */
  .mc-mat-chip {
    border-color: var(--catchup-blue);
    border-width: 2px;
    border-style: solid;
    border-radius: var(--rad-catchup-xlarge);
    background-color: var(--catchup-white);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1);
  }
  .mc-mat-textwrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin: 0.5rem;
    min-width: 200px;
    padding-left: 1rem;
    padding-right: 1rem;
  }
  .mc-mat-textwrap-ov {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin: 0.5rem;
    min-width: 200px;
    overflow: hidden;
    padding-left: 1rem;
    padding-right: 1rem;
  }
  .mc-mat-text { font-size: var(--fs-lg); white-space: pre-wrap; }
  .mc-mat-items {
    flex-shrink: 0;
    display: flex;
    flex-direction: row;
    gap: 1rem;
    overflow-x: auto;
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
  }
  .mc-mat-drag-item { transition: all 200ms cubic-bezier(0.4, 0, 0.2, 1); }
  .mc-mat-box {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-width: 2px;
    border-style: solid;
    border-radius: var(--rad-catchup-xlarge);
    cursor: pointer;
    transition: all 300ms cubic-bezier(0.4, 0, 0.2, 1);
  }
  .mc-mat-bd-sel { border-color: var(--catchup-blue); }
  .mc-mat-bd-def { border-color: var(--catchup-lighter-gray); }
  .mc-mat-row { display: flex; flex-direction: row; width: 100%; }
  .mc-mat-col3 { width: 33.333333%; }
  .mc-mat-keybox {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-width: 1.5px;
    border-style: solid;
    border-color: var(--catchup-blue-100);
    background: var(--catchup-white);
    border-radius: var(--rad-catchup-xlarge);
    transition: all 300ms cubic-bezier(0.4, 0, 0.2, 1);
    margin-top: 0.75rem;
    margin-bottom: 0.75rem;
  }
  .mc-mat-keyinner {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    transition: all 300ms cubic-bezier(0.4, 0, 0.2, 1);
    padding-left: 1rem;
    padding-right: 1rem;
    text-align: center;
  }
  .mc-mat-vdiv {
    margin-left: 1rem;
    margin-right: 1rem;
    width: 2px;
    background-color: var(--catchup-lighter-gray);
  }
  .mc-mat-dropzone {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-width: 1.5px;
    border-style: dashed;
    border-color: var(--catchup-blue-200);
    background: rgba(255, 255, 255, 0.45);
    -webkit-backdrop-filter: blur(8px); backdrop-filter: blur(8px);
    border-radius: var(--rad-catchup-xlarge);
    cursor: pointer;
    transition: all 300ms cubic-bezier(0.4, 0, 0.2, 1);
    margin-top: 0.75rem;
    margin-bottom: 0.75rem;
  }
  .mc-mat-droptarget {
    background-color: var(--catchup-light-blue);
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5);
  }
  .mc-mat-dropcontent {
    height: 100%;
    flex: 1 1 0%;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    padding-left: 1rem;
    padding-right: 1rem;
  }

  /* Grouping-specific (drag/drop into group buckets). Reuses mc-ord-drag,
     mc-mat-chip, mc-mat-bd-*, mc-mat-drag-item, mc-mat-row/col3/vdiv/keyinner/text,
     mc-ord-box-text/media + answer-state borders, mc-shrink0, mc-scroll. */
  .mc-2xl { font-size: var(--fs-2xl); }
  .mc-grp-text { font-size: var(--fs-xl); text-align: center; white-space: pre-wrap; }
  .mc-grp-textwrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin: 0.5rem;
    min-width: 200px;
  }
  .mc-grp-textwrap-ov {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin: 0.5rem;
    min-width: 200px;
    overflow-y: auto;
  }
  .mc-grp-items {
    flex-shrink: 0;
    display: flex;
    flex-direction: row;
    gap: 1rem;
    overflow-x: auto;
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
  }
  .mc-grp-keybox {
    border-color: var(--catchup-blue);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-width: 2px;
    border-style: solid;
    border-radius: var(--rad-catchup-xlarge);
    transition: all 300ms cubic-bezier(0.4, 0, 0.2, 1);
    margin-top: 0.75rem;
    margin-bottom: 0.75rem;
  }
  .mc-grp-h-text-outer { height: var(--h-catchup-activity-text-outer-box-item); }
  .mc-grp-h-media-outer { height: var(--h-catchup-activity-media-outer-box-item); }
  .mc-grp-cell { flex: 1 1 0%; min-width: 0; }
  .mc-grp-cell-pad { height: 100%; padding-top: 0.75rem; padding-bottom: 0.75rem; }
  .mc-grp-dropzone {
    flex: 1 1 0%;
    border-color: var(--catchup-blue);
    border-radius: var(--rad-catchup-xlarge);
    border-width: 2px;
    border-style: solid;
    height: 100%;
    padding: 0.25rem;
    transition: all 200ms cubic-bezier(0.4, 0, 0.2, 1);
  }
  .mc-grp-zone-scroll { height: 100%; width: 100%; overflow-x: auto; }
  .mc-grp-chips {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
    width: max-content;
    height: 100%;
  }
  .mc-grp-chip-pad { padding: 0.25rem; }
  .mc-grp-chipbox {
    border-width: 2px;
    border-style: solid;
    border-radius: var(--rad-catchup-xlarge);
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    transition: all 300ms cubic-bezier(0.4, 0, 0.2, 1);
    cursor: pointer;
  }
  .mc-grp-bd-empty { border-color: var(--catchup-lighter-gray); }
  .mc-grp-chiptext {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    transition: all 300ms cubic-bezier(0.4, 0, 0.2, 1);
    min-width: 200px;
    overflow-y: auto;
  }
  .mc-grp-m2 { margin: 0.5rem; }

  /* Grouping — design-prototype: word pool + grid of titled dashed-glass bins. */
  .mc-gp2 { display: flex; flex-direction: column; gap: var(--space-5); height: 100%; }
  .mc-gp2-poollabel { font-size: var(--fs-sm); font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--text-muted); }
  .mc-gp2-pool { display: flex; flex-wrap: wrap; gap: 10px; min-height: 44px; align-items: flex-start; }
  .mc-gp2-empty { color: var(--text-muted); font-size: var(--fs-sm); align-self: center; }
  .mc-gp2-chip, .mc-gp2-placed { cursor: pointer; font-weight: 700; font-size: var(--fs-base); padding: 9px 16px; border-radius: var(--rad-catchup-full); background: var(--catchup-white); border: 1.5px solid var(--catchup-blue-100); color: var(--brand-dark); transition: transform 150ms, border-color 150ms, box-shadow 150ms, background 150ms; }
  .mc-gp2-chip:hover { transform: translateY(-1px); border-color: var(--catchup-blue); box-shadow: 0 6px 14px -8px rgba(15,72,89,0.3); }
  .mc-gp2-chip.is-picked { background: var(--brand-50); border-color: var(--catchup-blue); box-shadow: 0 0 0 3px var(--brand-100, rgba(87,194,211,0.22)); }
  .mc-gp2-bins { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 12px; align-content: start; }
  /* V4 aas__group — white card bin with a clear title */
  .mc-gp2-bin {
    display: flex; flex-direction: column;
    padding: var(--space-4);
    border-radius: var(--rad-catchup-large);
    border: 1.5px solid var(--catchup-blue-100);
    background: var(--catchup-white);
    box-shadow: var(--shadow-card);
    cursor: pointer;
    transition: border-color 150ms, background 150ms, box-shadow 150ms;
  }
  .mc-gp2-bin.is-target {
    border: 1.5px solid var(--catchup-blue);
    background: var(--brand-50);
    box-shadow: 0 0 0 3px var(--brand-100, rgba(87,194,211,0.22));
  }
  .mc-gp2-bin.is-target:hover { box-shadow: 0 0 0 4px var(--brand-100, rgba(87,194,211,0.3)), 0 8px 18px -10px rgba(15,72,89,0.35); }
  .mc-gp2-bin.is-target .mc-gp2-binhint { color: var(--brand-dark); font-weight: 700; font-style: normal; }
  .mc-gp2-bintitle { font-weight: 700; font-size: var(--fs-lg); color: var(--brand-dark); text-align: center; margin-bottom: var(--space-3); }
  .mc-gp2-binbody { display: flex; flex-wrap: wrap; gap: var(--space-2); min-height: 64px; align-content: flex-start; }
  .mc-gp2-placed { font-size: var(--fs-md); padding: 6px var(--space-3); }
  /* graded recolor (V4 aas__grouped.is-correct/.is-wrong) */
  .mc-gp2-placed.is-correct {
    background: linear-gradient(160deg, rgba(122,203,164,0.32), rgba(94,170,130,0.18));
    border: 1.5px solid var(--color-success);
    color: var(--color-success-text);
    font-weight: 800;
    box-shadow: 0 0 0 3px var(--color-success-ring, rgba(70,150,95,0.22)), 0 8px 18px -10px rgba(46,125,85,0.5);
  }
  .mc-gp2-placed.is-wrong {
    background: var(--color-error-bg);
    border: 1.5px solid var(--color-error);
    color: var(--color-error-text);
    box-shadow: 0 0 0 3px var(--color-error-ring, rgba(217,97,63,0.18));
  }
  /* The key reveal: an item the learner never placed here. DASHED and unfilled
     so it can never be read as a chip they dropped — the same "outlined, not
     ticked" language MCMA uses for a correct option the learner missed. */
  .mc-gp2-placed.is-key {
    cursor: default;
    background: transparent;
    border: 1.5px dashed var(--color-success);
    color: var(--color-success-text);
    box-shadow: none;
  }
  .mc-gp2-legend { display: flex; flex-wrap: wrap; gap: var(--space-4); align-items: center; font-size: var(--fs-sm); color: var(--text-muted); }
  .mc-gp2-legend-item { display: inline-flex; align-items: center; gap: var(--space-2); }
  .mc-gp2-legend-swatch { width: 26px; height: 16px; padding: 0; border-radius: var(--rad-catchup-full); }
  /* empty-group hint (V4 aas__group-hint) */
  .mc-gp2-binhint { font-size: var(--fs-md); color: var(--text-muted); font-style: italic; align-self: center; margin: auto; }

  /* FillInTheBlanks-specific (drag option → blank). Reuses mc-ord-drag,
     mc-ord-chip-media, mc-dd-grid/cell/pad/row/num, mc-dd-placeholder-inner,
     mc-mat-drag-item, mc-ord-box-correct/-incorrect/-default, mc-option-body,
     mc-italic, mc-xl. rounded-lg → 0.5rem; bg-blue-50/ring-blue-400 were no-ops. */
  .mc-fib-chip {
    border-color: var(--catchup-blue);
    border-width: 2px;
    border-style: solid;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
    border-radius: var(--rad-catchup-xlarge);
    background-color: var(--catchup-white);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1);
  }
  .mc-fib-italic-text { font-style: italic; white-space: pre-wrap; }
  .mc-fib-options {
    width: 100%;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
  }
  .mc-fib-opt {
    border-color: var(--catchup-blue);
    border-width: 2px;
    border-style: solid;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
    border-radius: var(--rad-catchup-xlarge);
    cursor: pointer;
    user-select: none;
  }
  .mc-fib-opt-media {
    border-color: var(--catchup-blue);
    border-width: 2px;
    border-style: solid;
    padding: 0.25rem 0.5rem;
    border-radius: var(--rad-catchup-xlarge);
    cursor: pointer;
    user-select: none;
  }
  .mc-fib-dropzone { transition: all 200ms cubic-bezier(0.4, 0, 0.2, 1); border-radius: 0.5rem; }
  .mc-fib-droptarget { box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5); }
  .mc-fib-rel { position: relative; }
  .mc-fib-input {
    width: 100%;
    min-height: 44px;
    border-width: 1px;
    border-style: solid;
    border-radius: 0.5rem;
  }
  .mc-fib-input-filled {
    border-color: var(--catchup-blue-400);
    padding-left: 0.5rem;
    padding-right: 0.5rem;
    cursor: pointer;
  }
  .mc-fib-input-empty {
    background-color: var(--catchup-gray-50);
    border-color: var(--catchup-gray-200);
    border-style: dashed;
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
    padding-left: 1rem;
    padding-right: 1rem;
  }
  .mc-fib-pill {
    position: absolute;
    top: -10px;
    right: 1rem;
    background-color: var(--catchup-white);
  }
  .mc-fib-mediaph {
    width: var(--w-catchup-activity-media-box-item);
    height: var(--h-catchup-activity-media-box-item);
    border-width: 1px;
    border-style: dashed;
    border-radius: var(--rad-catchup-xlarge);
  }
  .mc-fib-media-filled { flex: 1 1 0%; cursor: pointer; }

  /* Ordering — up/down arrow rows (reliable reorder, replaces fixed-overlay drag) */
  .mc-ord2-row { display: flex; align-items: center; gap: var(--space-3); margin: var(--space-3) 0; }
  .mc-ord2-num {
    width: 30px; height: 30px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    border-radius: var(--rad-catchup-full);
    background: var(--catchup-blue); color: var(--catchup-white); font-weight: 700;
  }
  .mc-ord2-card {
    flex: 1 1 0%; min-width: 0;
    font-size: var(--fs-lg); font-weight: 600; color: var(--text-primary);
    padding: var(--space-3) var(--space-4);
    border: 1.5px solid var(--catchup-blue-200);
    border-radius: var(--rad-catchup-medium);
    background: var(--catchup-white);
  }
  /* answer-reveal coloring — scoped (higher specificity) so it wins over the base
     .mc-ord2-card border/background above. In showCorrectAnswer mode every row is
     the correct slot, so the full list turns green = "this is the right order". */
  .mc-ord2-card.mc-ord-box-correct {
    border-color: var(--color-success);
    background: var(--color-success-bg);
    color: var(--color-success-text);
  }
  .mc-ord2-card.mc-ord-box-incorrect {
    border-color: var(--color-error);
    background: var(--color-error-bg);
    color: var(--color-error-text);
  }
  /* match the index pill to the row state so the correct order reads at a glance */
  .mc-ord2-row:has(.mc-ord-box-correct) .mc-ord2-num { background: var(--color-success); }
  .mc-ord2-row:has(.mc-ord-box-incorrect) .mc-ord2-num { background: var(--color-error); }
  .mc-ord2-btns { display: flex; flex-direction: column; gap: 2px; flex-shrink: 0; }
  .mc-ord2-btns button {
    width: 30px; height: 22px;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; font-size: 10px;
    border: 1px solid var(--catchup-blue-100);
    background: var(--catchup-white); color: var(--brand-dark);
    border-radius: 6px;
  }
  .mc-ord2-btns button:disabled { opacity: 0.35; cursor: default; }
  .mc-ord2-btns button:not(:disabled):hover { background: var(--brand-50); border-color: var(--catchup-blue); }
  .mc-mat-drophint { color: var(--catchup-blue); font-weight: 700; font-size: var(--fs-2xl); }
`;
